import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Search, ChevronLeft, ChevronRight, Check, Printer, BookOpen, Sparkles,
  Menu, X, Users, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { openSamWithPrefill } from "@/components/SamHelper";

const TRACKS = [
  { id: "sales",   label: "Sales",   icon: Users,       testId: "playbook-tab-sales" },
  { id: "manager", label: "Manager", icon: ShieldCheck, testId: "playbook-tab-manager" },
];

// Owner / admin / super-admin default to Manager. Members default to Sales.
function defaultTrackForRole(role, isSuper) {
  if (isSuper) return "manager";
  if (role === "owner" || role === "admin") return "manager";
  return "sales";
}

export default function Playbook() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, org } = useAuth();

  // Track state — defaults are set after we know who's logged in.
  // We let the URL slug override (so a manager arriving via a shared sales
  // chapter link sees the sales tab automatically).
  const [track, setTrack] = useState(null);

  const [chaptersByTrack, setChaptersByTrack] = useState({ sales: null, manager: null });
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState([]);
  const [q, setQ] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // 1. Set initial track from role (only on first render once auth is ready)
  useEffect(() => {
    if (track !== null || !user) return;
    const role = org?.role || "member";
    setTrack(defaultTrackForRole(role, !!user.is_super_admin));
  }, [user, org, track]);

  // 2. Load chapter lists for BOTH tracks once (cheap — small payloads)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, m, prog] = await Promise.all([
          api.get("/playbook/chapters?track=sales"),
          api.get("/playbook/chapters?track=manager"),
          api.get("/playbook/progress"),
        ]);
        if (cancelled) return;
        setChaptersByTrack({ sales: s.data || [], manager: m.data || [] });
        setProgress(prog.data || []);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 3. If URL slug exists, sync track to whichever track owns the slug
  useEffect(() => {
    if (!slug) return;
    const inSales   = (chaptersByTrack.sales   || []).some((c) => c.slug === slug);
    const inManager = (chaptersByTrack.manager || []).some((c) => c.slug === slug);
    if (inSales && track !== "sales") setTrack("sales");
    else if (inManager && track !== "manager") setTrack("manager");
  }, [slug, chaptersByTrack, track]);

  // 4. Auto-pick first chapter of the active track when nothing in URL
  useEffect(() => {
    if (!track) return;
    const list = chaptersByTrack[track];
    if (!list || list.length === 0) return;
    if (!slug) {
      navigate(`/app/playbook/${list[0].slug}`, { replace: true });
    }
  }, [chaptersByTrack, slug, track, navigate]);

  // 5. Load active chapter content whenever slug changes
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setActive(null);
    (async () => {
      try {
        const r = await api.get(`/playbook/chapters/${slug}`);
        if (!cancelled) {
          setActive(r.data);
          window.scrollTo({ top: 0, behavior: "smooth" });
          setMobileNavOpen(false);
        }
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const chapters = (track && chaptersByTrack[track]) || [];

  const readSet = useMemo(() => new Set(progress.map((p) => p.chapter_id)), [progress]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return chapters;
    return chapters.filter(
      (c) =>
        c.title.toLowerCase().includes(needle) ||
        (c.summary || "").toLowerCase().includes(needle),
    );
  }, [chapters, q]);

  const currentIdx = chapters.findIndex((c) => c.slug === slug);
  const prevChapter = currentIdx > 0 ? chapters[currentIdx - 1] : null;
  const nextChapter = currentIdx >= 0 && currentIdx < chapters.length - 1
    ? chapters[currentIdx + 1] : null;

  const toggleRead = useCallback(async (chapterId, makeRead) => {
    try {
      if (makeRead) {
        await api.post(`/playbook/progress/${chapterId}`);
        setProgress((p) => [...p, { chapter_id: chapterId, completed_at: new Date().toISOString() }]);
      } else {
        await api.delete(`/playbook/progress/${chapterId}`);
        setProgress((p) => p.filter((x) => x.chapter_id !== chapterId));
      }
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  const switchTrack = useCallback((tid) => {
    if (tid === track) return;
    setTrack(tid);
    const list = chaptersByTrack[tid] || [];
    if (list.length > 0) {
      navigate(`/app/playbook/${list[0].slug}`);
    }
  }, [track, chaptersByTrack, navigate]);

  if (!track || (chaptersByTrack[track] === null)) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-white/45">
        Loading playbook…
      </div>
    );
  }

  // Per-track read count (active-track only — matches what the user sees in the rail)
  const readCount = chapters.filter((c) => readSet.has(c.id)).length;
  const total = chapters.length;

  return (
    <div className="relative" data-testid="playbook-page" data-track={track}>
      {/* Mobile open-nav floating button */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        className="fixed bottom-24 right-4 z-30 inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0a0a0a]/95 px-4 py-2 text-xs text-white shadow-xl backdrop-blur lg:hidden"
        data-testid="playbook-mobile-nav-open"
      >
        <Menu className="h-4 w-4" /> Chapters
      </button>

      <div className="flex">
        {/* LEFT RAIL */}
        <aside
          className={`${
            mobileNavOpen
              ? "fixed inset-0 z-40 flex w-full bg-[#0a0a0a]/95 backdrop-blur"
              : "hidden"
          } lg:sticky lg:top-16 lg:flex lg:h-[calc(100vh-4rem)] lg:w-72 lg:flex-shrink-0 lg:border-r lg:border-white/5 lg:bg-transparent lg:backdrop-blur-none`}
          data-testid="playbook-nav"
        >
          <div className="flex h-full w-full flex-col px-4 py-5 lg:w-72">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-eyebrow text-indigo-300">
                <BookOpen className="h-3.5 w-3.5" /> Playbook
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="rounded p-1 text-white/55 hover:bg-white/5 lg:hidden"
                aria-label="Close chapters"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Track tabs */}
            <div
              className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-white/8 bg-white/[0.02] p-1"
              data-testid="playbook-track-tabs"
              role="tablist"
            >
              {TRACKS.map(({ id, label, icon: Icon, testId }) => {
                const isActive = track === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => switchTrack(id)}
                    role="tab"
                    aria-selected={isActive}
                    data-testid={testId}
                    data-active={isActive}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition ${
                      isActive
                        ? "bg-white text-black"
                        : "text-white/60 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="mb-3 flex items-center gap-2 text-xs text-white/55">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all"
                  style={{ width: total ? `${(readCount / total) * 100}%` : "0%" }}
                />
              </div>
              <span className="whitespace-nowrap text-[11px]" data-testid="playbook-progress-label">
                {readCount} of {total} read
              </span>
            </div>

            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
              <Input
                placeholder={`Search ${track === "sales" ? "sales" : "manager"} chapters…`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-9 border-white/10 bg-white/[0.03] pl-8 text-sm text-white placeholder:text-white/35"
                data-testid="playbook-search"
              />
            </div>

            <nav className="flex-1 overflow-y-auto pr-1" data-testid="playbook-chapter-list">
              {filtered.map((c) => {
                const isActive = c.slug === slug;
                const isRead = readSet.has(c.id);
                return (
                  <Link
                    key={c.id}
                    to={`/app/playbook/${c.slug}`}
                    className={`mb-1 block rounded-lg border px-3 py-2.5 transition ${
                      isActive
                        ? "border-indigo-500/30 bg-indigo-500/[0.08] text-white"
                        : "border-transparent text-white/70 hover:border-white/5 hover:bg-white/[0.03] hover:text-white"
                    }`}
                    data-testid={`playbook-nav-${c.slug}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] ${
                        isRead ? "bg-emerald-500/80 text-white" : "bg-white/[0.06] text-white/55"
                      }`}>
                        {isRead ? <Check className="h-3 w-3" /> : c.chapter_number}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm">{c.title}</span>
                    </div>
                  </Link>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-white/40">No chapters match.</div>
              )}
            </nav>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="min-w-0 flex-1">
          {/* Sticky top bar */}
          <div className="sticky top-16 z-10 -mx-4 mb-6 border-b border-white/5 bg-[#050505]/85 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 print:hidden">
            <div className="mx-auto flex max-w-[760px] items-center justify-between gap-3">
              <div className="min-w-0">
                {active ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">
                      {track === "sales" ? "Sales" : "Manager"} · Chapter {active.chapter_number} of {total}
                    </div>
                    <div className="truncate text-sm font-medium text-white" data-testid="playbook-active-title">
                      {active.title}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-white/45">Loading…</div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!prevChapter}
                  onClick={() => prevChapter && navigate(`/app/playbook/${prevChapter.slug}`)}
                  className="h-8 px-2 text-white/70 hover:bg-white/5 hover:text-white disabled:opacity-30"
                  data-testid="playbook-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="ml-1 hidden text-xs sm:inline">Prev</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!nextChapter}
                  onClick={() => nextChapter && navigate(`/app/playbook/${nextChapter.slug}`)}
                  className="h-8 px-2 text-white/70 hover:bg-white/5 hover:text-white disabled:opacity-30"
                  data-testid="playbook-next"
                >
                  <span className="mr-1 hidden text-xs sm:inline">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Chapter body */}
          <article
            className="mx-auto w-full max-w-[760px] px-1 pb-24 sm:px-6"
            data-testid="playbook-chapter-body"
          >
            {!active ? (
              <div className="py-24 text-center text-white/45">Loading chapter…</div>
            ) : (
              <>
                <ChapterMarkdown content={active.content_md || ""} />

                {/* Mark-as-read + Ask Sam + Print */}
                <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/8 pt-6 print:hidden">
                  <label
                    className="flex cursor-pointer items-center gap-2 text-sm text-white/70 hover:text-white"
                    data-testid="playbook-mark-read"
                  >
                    <Checkbox
                      checked={readSet.has(active.id)}
                      onCheckedChange={(v) => toggleRead(active.id, !!v)}
                      className="border-white/30"
                    />
                    Mark as read
                  </label>

                  <div className="flex items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => openSamWithPrefill(
                        `I'm a ${track === "sales" ? "sales rep" : "manager"} — can you explain "${active.title}" in simpler terms?`
                      )}
                      className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500/10 px-3 py-1.5 text-xs text-indigo-200 ring-1 ring-inset ring-indigo-500/20 hover:bg-indigo-500/20"
                      data-testid="playbook-ask-sam"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Ask Sam about this
                    </button>
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white"
                      data-testid="playbook-print"
                    >
                      <Printer className="h-3.5 w-3.5" /> Print this chapter
                    </button>
                  </div>
                </div>

                {/* Prev/Next footer */}
                <div className="mt-10 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between print:hidden">
                  {prevChapter ? (
                    <Link
                      to={`/app/playbook/${prevChapter.slug}`}
                      className="inline-flex items-center gap-2 rounded-xl border border-white/8 px-4 py-3 text-white/70 hover:border-white/15 hover:text-white"
                      data-testid="playbook-footer-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/35">Previous</div>
                        <div className="text-sm">{prevChapter.title}</div>
                      </div>
                    </Link>
                  ) : <span />}
                  {nextChapter ? (
                    <Link
                      to={`/app/playbook/${nextChapter.slug}`}
                      className="inline-flex items-center gap-2 self-end rounded-xl border border-white/8 px-4 py-3 text-right text-white/70 hover:border-white/15 hover:text-white"
                      data-testid="playbook-footer-next"
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/35">Next</div>
                        <div className="text-sm">{nextChapter.title}</div>
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  ) : <span />}
                </div>
              </>
            )}
          </article>
        </main>
      </div>
    </div>
  );
}

// Markdown renderer with explicit element styling (no Tailwind typography plugin
// required). Tuned for the dark-on-dark reading surface.
const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className="font-display mt-2 mb-6 text-4xl font-light leading-tight tracking-tight text-white">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="font-display mt-12 mb-4 text-2xl font-medium leading-tight tracking-tight text-white">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-display mt-8 mb-3 text-lg font-medium leading-snug text-white">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-6 mb-2 text-base font-medium text-white">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="my-4 text-[15px] leading-[1.75] text-white/80">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 list-disc space-y-1.5 pl-6 text-[15px] leading-[1.75] text-white/80 marker:text-white/35">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 list-decimal space-y-1.5 pl-6 text-[15px] leading-[1.75] text-white/80 marker:text-white/35">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-medium text-white">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-white/85">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-indigo-300 underline-offset-4 hover:underline"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-6 border-l-2 border-indigo-500/40 bg-white/[0.02] py-1 pl-4 text-[15px] leading-[1.75] text-white/75">
      {children}
    </blockquote>
  ),
  code: ({ inline, className, children, ...rest }) => {
    if (inline) {
      return (
        <code
          className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[0.85em] text-indigo-200"
          {...rest}
        >
          {children}
        </code>
      );
    }
    return (
      <pre className="my-5 overflow-x-auto rounded-lg border border-white/8 bg-black/40 p-4 text-[13px] leading-relaxed text-white/85">
        <code className={className} {...rest}>
          {children}
        </code>
      </pre>
    );
  },
  hr: () => <hr className="my-10 border-white/8" />,
  table: ({ children }) => (
    <div className="my-6 overflow-x-auto">
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-white/10 px-3 py-2 font-medium text-white">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-white/5 px-3 py-2 text-white/75">{children}</td>
  ),
};

function ChapterMarkdown({ content }) {
  return (
    <div className="max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
